<template>
  <div class="payments-settings">
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          <CreditCard :size="20" class="me-2" />
          Square Payment Configuration
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loadState.IsLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveSettings">
          <div class="alert alert-info mb-4">
            <i class="fas fa-info-circle me-2"></i>
            Configure your Square payment settings to enable online invoice payments. 
            You'll need a Square account and API credentials.
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Square Application ID</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.applicationId"
                placeholder="sq0idp-xxxxx"
              />
              <small class="text-muted">Your Square application ID</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Square Location ID</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.locationId"
                placeholder="L123456"
              />
              <small class="text-muted">Your Square location ID</small>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <label class="form-label">Square Access Token</label>
              <input 
                type="password" 
                class="form-control" 
                v-model="settings.accessToken"
                placeholder="••••••••••••••••"
              />
              <small class="text-muted">Your Square access token (kept secure)</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Environment</label>
              <select class="form-select" v-model="settings.environment">
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production (Live)</option>
              </select>
              <small class="text-muted">Use Sandbox for testing</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Currency</label>
              <select class="form-select" v-model="settings.currency">
                <option value="USD">USD - US Dollar</option>
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="EUR">EUR - Euro</option>
              </select>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Webhook URL</label>
            <div class="input-group">
              <input 
                type="text" 
                class="form-control" 
                :value="webhookUrl"
                readonly
              />
              <button 
                type="button" 
                class="btn btn-outline-secondary"
                @click="copyWebhookUrl"
              >
                <Copy :size="16" />
              </button>
            </div>
            <small class="text-muted">
              Configure this URL in your Square Developer Dashboard for payment webhooks
            </small>
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              id="enablePayments" 
              v-model="settings.enabled"
            />
            <label class="form-check-label" for="enablePayments">
              <strong>Enable Square Payments</strong>
            </label>
            <small class="text-muted d-block">
              Allow customers to pay invoices online via Square
            </small>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary" @click="testConnection" :disabled="testState.IsLoading">
              <Zap :size="16" class="me-1" />
              {{ testState.IsLoading ? 'Testing...' : 'Test Connection' }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saveState.IsLoading">
              <Save :size="16" class="me-1" />
              {{ saveState.IsLoading ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { CreditCard, Save, Copy, Zap } from 'lucide-vue-next';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import { useApiRequest } from '@/composables';

const { state: loadState, execute: executeLoad } = useApiRequest('payments:load');
const { state: saveState, execute: executeSave } = useApiRequest('payments:save');
const { state: testState, execute: executeTest } = useApiRequest('payments:test');

const settings = ref({
  applicationId: '',
  locationId: '',
  accessToken: '',
  environment: 'sandbox',
  currency: 'USD',
  enabled: false
});

const webhookUrl = computed(() => {
  return `${window.location.origin}/api/payments/square/webhook`;
});

const loadSettings = async () => {
  await executeLoad(() => ApiService.getOptions('SquarePayments'));
  if (loadState.value.IsError) {
    Alert.notify({ text: 'Failed to load payment settings', type: 'error' });
    return;
  }
  const options: any[] = loadState.value.Data || [];
  settings.value = {
    applicationId: options.find((o: any) => o.Key === 'SquareApplicationId')?.Value || '',
    locationId: options.find((o: any) => o.Key === 'SquareLocationId')?.Value || '',
    accessToken: options.find((o: any) => o.Key === 'SquareAccessToken')?.Value || '',
    environment: options.find((o: any) => o.Key === 'SquareEnvironment')?.Value || 'sandbox',
    currency: options.find((o: any) => o.Key === 'SquareCurrency')?.Value || 'USD',
    enabled: options.find((o: any) => o.Key === 'SquarePaymentsEnabled')?.Value === 'true'
  };
};

const saveSettings = async () => {
  await executeSave(() => ApiService.saveOptions('SquarePayments', [
    { Key: 'SquareApplicationId', Value: settings.value.applicationId },
    { Key: 'SquareLocationId', Value: settings.value.locationId },
    { Key: 'SquareAccessToken', Value: settings.value.accessToken },
    { Key: 'SquareEnvironment', Value: settings.value.environment },
    { Key: 'SquareCurrency', Value: settings.value.currency },
    { Key: 'SquarePaymentsEnabled', Value: settings.value.enabled.toString() }
  ]));
  if (saveState.value.IsError) {
    Alert.notify({ text: 'Failed to save payment settings', type: 'error' });
  } else {
    Alert.notify({ text: 'Payment settings saved successfully', type: 'success' });
  }
};

const testConnection = async () => {
  await executeTest(() => ApiService.testSquareConnection());
  if (testState.value.IsError) {
    Alert.notify({ text: 'Failed to connect to Square. Check your credentials.', type: 'error' });
  } else {
    Alert.notify({ text: 'Square connection successful', type: 'success' });
  }
};

const copyWebhookUrl = () => {
  navigator.clipboard.writeText(webhookUrl.value);
  Alert.notify({ text: 'Webhook URL copied to clipboard', type: 'success' });
};

onMounted(() => {
  loadSettings();
});
</script>
